import { Card, Grid, Tooltip, styled } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import hexToRGB from "utils/hexToRGB";
import AcxLoadingIndicator from "./AcxLoadingIndicator";
import { useTheme } from "@mui/material";
import { sxClsx } from "utils/styleClsx";

// Styled component for icon wrapper (used more than 3 times)
const IconWrapper = styled("div")({
    width: "32px",
    height: "32px",
});

// Helper for font color
function determineFontColor(props) {
    if (props.severity === "infoV2") {
        return "#1E3A8A";
    } else if (props.severity === "error" || props.severity === "errorV2") {
        return "#B91C1C";
    } else {
        return "#000000";
    }
}

type Severity = "success" | "info" | "error" | "loading" | "infoV2" | "errorV2";

type Props = {
    title: string;
    message: string;
    severity?: Severity;
    fullWidth?: boolean;
    variant?: "outlined";
    onClose?: () => void;
    fullWidthMessage?: boolean;
    wrapText?: boolean;
};

function AcxToast(props: Props) {
    const theme = useTheme();

    function _renderIcon() {
        if (props.severity === undefined) {
            return null;
        }
        let content: React.ReactElement;
        if (props.severity === "loading") {
            content = (
                <AcxLoadingIndicator
                    size={20}
                    alternate="PuffLoader"
                    color="secondary"
                />
            );
        } else if (props.severity === "infoV2") {
            content = (
                <InfoOutlinedIcon
                    sx={{
                        margin: "6px",
                        fontSize: "20px",
                        position: "relative",
                        color: (theme) => theme.palette.blue.main,
                    }}
                />
            );
        } else if (props.severity === "info") {
            content = (
                <InfoIcon
                    sx={{
                        margin: "6px",
                        fontSize: "20px",
                        color: (theme) => theme.palette.blue.main,
                    }}
                />
            );
        } else if (props.severity === "success") {
            content = (
                <CheckIcon
                    sx={{
                        margin: "6px",
                        fontSize: "20px",
                        color: (theme) => theme.palette.green.main,
                    }}
                />
            );
        } else if (props.severity === "errorV2") {
            content = (
                <InfoOutlinedIcon
                    sx={{
                        margin: "6px",
                        fontSize: "20px",
                        position: "relative",
                        color: (theme) => theme.palette.red.main,
                    }}
                    fontSize="small"
                />
            );
        } else {
            content = (
                <WarningIcon
                    sx={{
                        margin: "6px",
                        fontSize: "20px",
                        color: (theme) => theme.palette.error[700],
                    }}
                />
            );
        }
        return <IconWrapper>{content}</IconWrapper>;
    }

    function _renderCloseIcon() {
        return <CloseIcon onClick={props.onClose} />;
    }

    const isV2 = props.severity === "infoV2" || props.severity === "errorV2";

    return (
        <Card
            variant={props.variant || "elevation"}
            sx={sxClsx(
                { display: "flex", alignItems: "center", position: "relative" },
                props.fullWidth && {
                    width: "100%",
                    height: props.wrapText ? "fit-content" : "40px",
                    backgroundColor:
                        props.fullWidthMessage && props.severity === "error"
                            ? "#FEE2E2"
                            : undefined,
                    padding:
                        props.fullWidthMessage && props.severity === "error"
                            ? "0px 8px"
                            : undefined,
                    boxShadow:
                        props.severity === "infoV2" ||
                        props.severity === "errorV2"
                            ? "none!important"
                            : undefined,
                },
                !props.fullWidth && {
                    width: "327px",
                    height: props.wrapText ? undefined : "40px",
                },
                props.severity === "infoV2" && {
                    backgroundColor: "#DBEAFE !important",
                    borderRadius: "4px",
                    margin: "0px",
                },
                props.severity === "errorV2" && {
                    backgroundColor: "#FEE2E2 !important",
                    color: "#B91C1C",
                    borderRadius: "4px",
                    margin: "0px",
                },
            )}
        >
            <Grid
                container
                item
                alignItems="center"
                sx={sxClsx(
                    {
                        flexWrap: "nowrap",
                    },
                    props.severity === "infoV2" && {
                        backgroundColor: "#DBEAFE !important",
                        borderRadius: "4px",
                        margin: "0px",
                    },
                    props.severity === "errorV2" && {
                        backgroundColor: "#FEE2E2 !important",
                        color: "#B91C1C",
                        borderRadius: "4px",
                        margin: "0px",
                    },
                )}
            >
                <Grid item>{_renderIcon()}</Grid>
                <Grid
                    item
                    sx={{ width: props.fullWidthMessage ? "95%" : undefined }}
                >
                    <Tooltip title={props.title}>
                        <div
                            style={{
                                fontSize: "12px",
                                color: theme.palette.black.main,
                                fontWeight: "bold",
                                maxWidth: "250px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "block",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {props.title}
                        </div>
                    </Tooltip>
                    <Tooltip title={props.message}>
                        <div
                            style={{
                                fontSize: "12px",
                                color: hexToRGB(theme.palette.black.main, 0.5),
                                maxWidth: "250px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "block",
                                whiteSpace: props.wrapText
                                    ? "normal"
                                    : "nowrap",
                                marginTop: props.fullWidthMessage
                                    ? "4px"
                                    : undefined,
                                marginBottom: props.fullWidthMessage
                                    ? "4px"
                                    : undefined,
                                ...(props.fullWidthMessage && {
                                    maxWidth: "95%",
                                    color: determineFontColor(props),
                                }),
                            }}
                        >
                            {props.message}
                        </div>
                    </Tooltip>
                </Grid>
                {props.onClose && (
                    <Grid
                        item
                        sx={
                            !isV2
                                ? {
                                      position: "absolute",
                                      right: 0,
                                      top: "20%",
                                      width: "2rem",
                                      color: determineFontColor(props),
                                  }
                                : {
                                      position: "absolute",
                                      right: "35px",
                                      top: "3px",
                                      color: determineFontColor(props),
                                  }
                        }
                    >
                        {_renderCloseIcon()}
                    </Grid>
                )}
            </Grid>
        </Card>
    );
}

export default AcxToast;
